import type {Option, Options} from './types';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import logger from 'src/utils/logger';
import {groupBy, sortBy, flat} from 'src/utils/iterable';


const proceedKeys = new Set(['Tab', 'Enter', ',']);

export type SenseTypeaheadProps<V = Option> = {
  options: V[];
  searchable?: boolean;
  excludedValues?: V[];
  allowArbitraryValues?: boolean;
  showSugggestionOnPaste?: boolean;
  allowMultiArbitraryValues?: boolean;
  validateArbitraryValue?: (arg1: string) => boolean;
  makeArbitraryValue?: (arg1: string) => V;
  formatArbitraryOptionLabel?: (arg1: string) => string;
  searchOnEmptyString?: boolean;
  groupOptionsBy?: (arg1: V) => string;
  sortOptionsBy?: (arg1: V, arg2: V) => number;
  onInputChange?: (arg1: string) => unknown;
  onInputFocus?: () => unknown;
  isArbitrary?: (arg1: V) => boolean;
  resolveKey: (arg1: V) => string;
  resolveLabel: (
    arg1: V,
  ) => string | React.ReactElement<React.ComponentProps<'div'>>;
};

export function useSenseTypeahead<V>({
  options,
  onAddOption,
  searchable,
  isLimited,
  excludedValues = [],
  allowArbitraryValues,
  showSugggestionOnPaste,
  allowMultiArbitraryValues,
  validateArbitraryValue = () => true,

  // @ts-expect-error - TS2322 - Type '(searchTerm: string) => { value: string; label: string; arbitrary: boolean; }' is not assignable to type '(arg1: string) => V'.
  makeArbitraryValue = (searchTerm: string) => ({
    value: searchTerm,
    label: searchTerm,
    arbitrary: true,
  }),

  // @ts-expect-error - TS2339 - Property 'arbitrary' does not exist on type 'V'.
  isArbitrary = (option: V) => option.arbitrary,
  formatArbitraryOptionLabel = (value: string) => `Create "${value}"`,
  searchOnEmptyString = true,
  groupOptionsBy,
  sortOptionsBy,
  onInputChange,
  onInputFocus,
  resolveKey,
  resolveLabel,
}: SenseTypeaheadProps<V> & {
  onAddOption: (arg1: V) => unknown;
  isLimited?: boolean;
}): [
  TypeaheadState,
  V[],
  Map<string, V[]>,
  {
    readOnly: boolean | null | undefined;
    value: string;
    onChange: (arg1: React.SyntheticEvent<HTMLInputElement>) => unknown;
    onBlur: (arg1: React.SyntheticEvent<HTMLInputElement>) => unknown;
    onFocus: (arg1: React.SyntheticEvent<HTMLInputElement>) => unknown;
    onKeyDown: (arg1: React.KeyboardEvent<HTMLInputElement>) => unknown;
    ref: {
      current: HTMLInputElement | null | undefined;
    };
    onPaste?: (event: ClipboardEvent) => unknown;
  },
  (arg1: React.MouseEvent<HTMLInputElement>) => void,
  (arg1: V) => void,
  {
    isSearching: boolean;
    focused: boolean;
    isShowingOptions: boolean;
  },
] {
  const {t} = useLabelTranslation();
  const [state, dispatch] = React.useReducer(
    reduceWithSearchTerm,
    null,
    getDefaultState,
  );
  const {searchTerm, focused} = state;
  const trimmedSearchTerm = searchTerm.trim();

  // @ts-expect-error - TS7030 - Not all code paths return a value.
  const makeMultiArbitraryValue = (searchTerm: string) => {
    // @ts-expect-error - TS1501 - This regular expression flag is only available when targeting 'es2018' or later.
    const regex = /^(?=.*[,\n]).+$/s;
    if (regex.test(searchTerm)) {
      return {
        value: searchTerm,
        label: searchTerm,
        multiArbitrary: true,
      };
    }
  };

  const handleSelect = (option: V) => {
    dispatch({type: 'search', payload: ''});
    if (isShowingOptions) {
      onAddOption(option);
    }
  };

  let [filteredOptions, groupedOptions] = useFilteredOptions({
    searchTerm,
    options,
    excludedValues,
    groupOptionsBy,
    sortOptionsBy,
    resolveKey,
    resolveLabel,
  });

  // @ts-expect-error - TS2322 - Type 'false | "" | V | undefined' is not assignable to type 'false | V | null | undefined'.
  const arbitraryOption: V | false | undefined | null =
    allowArbitraryValues &&
    trimmedSearchTerm &&
    !options.some((option) => resolveKey(option) === trimmedSearchTerm) &&
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'V'.
    !excludedValues.includes(trimmedSearchTerm) &&
    validateArbitraryValue(trimmedSearchTerm) &&
    makeArbitraryValue(trimmedSearchTerm);

  if (arbitraryOption) {
    filteredOptions = [arbitraryOption, ...filteredOptions];
    // TODO (kyle): should actions even be in the groups?
    groupedOptions = new Map<string, V[]>([
      [t('actions', 'actions'), [arbitraryOption]],
      ...groupedOptions,
    ]);
  }

  // @ts-expect-error - TS2322 - Type 'false | "" | { value: string; label: string; multiArbitrary: boolean; } | null | undefined' is not assignable to type 'false | V | null | undefined'.
  const multiArbitraryOption: V | false | undefined | null =
    arbitraryOption &&
    allowMultiArbitraryValues &&
    showSugggestionOnPaste &&
    trimmedSearchTerm &&
    !options.some((option) => resolveKey(option) === trimmedSearchTerm) &&
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'V'.
    !excludedValues.includes(trimmedSearchTerm) &&
    validateArbitraryValue(trimmedSearchTerm) &&
    makeMultiArbitraryValue(trimmedSearchTerm);

  if (multiArbitraryOption) {
    filteredOptions = [...filteredOptions, multiArbitraryOption];
    const currentActions = groupedOptions.get('actions') || [];
    groupedOptions.set('actions', [...currentActions, multiArbitraryOption]);
  }

  const isSearching = Boolean(focused && searchable && !isLimited);
  const isShowingOptions = Boolean(
    focused &&
      // TODO (kyle): why is this here?
      !isLimited &&
      filteredOptions.length > 0 &&
      (trimmedSearchTerm || searchOnEmptyString),
  );

  const [{onBlur, ...inputPropsFromUseTypeahead}, handleContainerMouseDown] =
    useTypeahead({
      state,
      dispatch,
      options: filteredOptions,
      onAddOption: handleSelect,
      onInputFocus,
    });

  let inputProps = {
    readOnly: !isSearching,
    value: searchTerm,
    onChange: (event: React.SyntheticEvent<HTMLInputElement>) => {
      const {
        currentTarget: {value},
      } = event;
      dispatch({
        type: 'search',
        payload: value,
      });
      onInputChange && onInputChange(value);
    },
    onBlur: (event: React.SyntheticEvent<HTMLInputElement>) => {
      if (arbitraryOption) {
        handleSelect(arbitraryOption);
      }
      onBlur(event);
    },
    ...inputPropsFromUseTypeahead,
  };

  if (showSugggestionOnPaste) {
    inputProps = {
      ...inputProps,
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'onPaste' does not exist in type '{ onFocus: (_event: SyntheticEvent<HTMLInputElement, Event>) => void; onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void; ... 4 more ...; onBlur: (event: SyntheticEvent<...>) => void; }'.
      onPaste: (event: ClipboardEvent) => {
        if (!allowMultiArbitraryValues) {
          return;
        }
        event.preventDefault();
        const value = event.clipboardData?.getData('text');
        if (value && value.length) {
          dispatch({type: 'search', payload: value});
          onInputChange && onInputChange(value);
        }
      },
    };
  }

  return [
    state,
    filteredOptions,
    groupedOptions,
    inputProps,
    handleContainerMouseDown,
    handleSelect,
    {
      isSearching,
      focused,
      isShowingOptions,
    },
  ];
}
export function useFilteredOptions<V>({
  searchTerm,
  options,
  excludedValues = [],
  groupOptionsBy,
  sortOptionsBy,
  // @ts-expect-error - TS2339 - Property 'label' does not exist on type 'V'.
  resolveLabel = (option: V) => option.label,
  // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'V'.
  resolveKey = (option: V) => option.value,

  searchOptionsBy = (option: V, searchTerm: string) => {
    // debugger;
    // let labelContains = false,
    //   keyContains = false;
    const resolvedLabelRaw = resolveLabel(option);
    const resolvedLabel =
      typeof resolvedLabelRaw === 'string' ? resolvedLabelRaw : String(option);
    const resolvedKey = resolveKey(option);
    if (resolvedKey == null) {
      logger.warning(`Failed to resolve key for option`, option);
    }
    if (resolvedLabel == null) {
      logger.warning(`Failed to resolve label for following option`, option);
    }
    const labelContains =
      resolvedLabel?.toLowerCase().includes(searchTerm) ?? false;
    const keyContains =
      resolvedKey?.toLowerCase().includes(searchTerm) ?? false;

    return labelContains || keyContains;
  },
}: {
  searchTerm: string;
  options: V[];
  excludedValues: V[];
  groupOptionsBy?: (arg1: V) => string;
  sortOptionsBy?: (arg1: V, arg2: V) => number;
  resolveLabel?: (
    arg1: V,
  ) => string | React.ReactElement<React.ComponentProps<'div'>>;
  resolveKey?: (arg1: V) => string;
  searchOptionsBy?: (arg1: V, arg2: string) => boolean;
}): [V[], Map<string, V[]>] {
  // @ts-expect-error - TS2322 - Type '[V[], Map<any, any> | ConstructableIterable<[any]>]' is not assignable to type '[V[], Map<string, V[]>]'.
  return React.useMemo(() => {
    const trimmedValue = searchTerm.trim().toLowerCase();
    // NOTE (kyle): this is not performance sensitive but could be in the future. could use caching/indexing if needed.
    let filteredOptions: V[] = options
      ? options.filter((option) => !excludedValues.includes(option))
      : [];

    if (trimmedValue) {
      filteredOptions = filteredOptions.filter((option) =>
        searchOptionsBy(option, trimmedValue),
      );
    }

    if (sortOptionsBy) {
      filteredOptions = filteredOptions.slice().sort(sortOptionsBy);
    }

    let groupedOptions;
    if (groupOptionsBy) {
      groupedOptions = sortBy(
        // @ts-expect-error - TS2345 - Argument of type 'Map<string, V[]>' is not assignable to parameter of type 'ConstructableIterable<[any]>'.
        groupBy(filteredOptions, groupOptionsBy),
        ([key1]: [any], [key2]: [any]) => key1.localeCompare(key2),
      );
      // @ts-expect-error - TS2339 - Property 'values' does not exist on type 'ConstructableIterable<[any]>'.
      filteredOptions = Array.from(flat(groupedOptions.values()));
    } else {
      groupedOptions = new Map();
    }

    return [filteredOptions, groupedOptions];
  }, [searchTerm, options, excludedValues, sortOptionsBy]);
}

type InputProps = {
  onBlur: (_event: React.SyntheticEvent<HTMLInputElement>) => void;
  onFocus: (_event: React.SyntheticEvent<HTMLInputElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  ref: {
    current: HTMLInputElement | null | undefined;
  };
  readOnly?: boolean | null | undefined;
};

export function useTypeahead<V>({
  state,
  dispatch,
  options,
  onAddOption,
  onInputFocus,
  // @ts-expect-error - TS2339 - Property 'disabled' does not exist on type 'V'.
  isOptionDisabled = (option: V) => option.disabled,
}: {
  state: TypeaheadState;
  dispatch: (arg1: TypeaheadAction) => void;
  options: V[];
  onAddOption: (arg1: V) => unknown;
  onInputFocus?: () => unknown;
  isOptionDisabled?: (arg1: V) => boolean;
}): [InputProps, (arg1: React.MouseEvent<HTMLInputElement>) => void] {
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const inputElement = React.useRef<HTMLInputElement | null | undefined>();
  const {selectedIndex, focused} = state;

  /*
  React.useEffect(() => {
    setState({...state, selectedIndex: 0});
  }, [filteredOptions]);
  */

  const handleFocus = (_event: React.SyntheticEvent<HTMLInputElement>) => {
    dispatch({type: 'focus'});
    onInputFocus && onInputFocus();
  };

  const handleBlur = (_event: React.SyntheticEvent<HTMLInputElement>) => {
    dispatch({type: 'blur'});
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    ///const value = event.currentTarget.value;
    const key = event.key;
    //const text = value.trim();

    if (proceedKeys.has(key)) {
      //if (isShowingOptions || key !== 'Tab') {
      //event.preventDefault();

      if (
        options &&
        options.length > 0
        //selectionKeys.has(key)
      ) {
        event.preventDefault();
        onAddOption(options[selectedIndex]);
      } else {
        // TODO (kyle): not sure we want this
        //onAddOption({value, label: value, arbitrary: true});
      }
      //}
    } else if (key === 'ArrowDown') {
      event.preventDefault();
      let nextSelectedIndex = selectedIndex + 1;

      while (
        nextSelectedIndex < options.length &&
        options[nextSelectedIndex] != null &&
        isOptionDisabled(options[nextSelectedIndex])
      ) {
        nextSelectedIndex++;
      }

      if (nextSelectedIndex < options.length) {
        dispatch({type: 'select_index', payload: nextSelectedIndex});
      }
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      let nextSelectedIndex = selectedIndex - 1;

      while (
        nextSelectedIndex >= 0 &&
        options[nextSelectedIndex] != null &&
        isOptionDisabled(options[nextSelectedIndex])
      ) {
        nextSelectedIndex--;
      }

      if (nextSelectedIndex >= 0) {
        dispatch({type: 'select_index', payload: nextSelectedIndex});
      }
    } else if (key === 'Escape') {
      event.preventDefault();
      inputElement.current && inputElement.current.blur();
    }
  };

  const handleContainerMouseDown = (
    event: React.SyntheticEvent<HTMLElement>,
  ) => {
    const input = inputElement.current;
    if (focused && input === document.activeElement && event.target !== input) {
      event.preventDefault();
    } else if (input && !focused) {
      event.preventDefault();
      input.focus();
      input.select();
    }
  };

  return [
    {
      ref: inputElement,
      onKeyDown: handleInputKeyDown,
      onFocus: handleFocus,
      onBlur: handleBlur,
    },
    handleContainerMouseDown,
  ];
}

export function useTokenInput({
  onAddValue,
  onBackspace,
  onInputFocus,
}: {
  onAddValue: (arg1: string) => unknown;
  onBackspace: () => unknown;
  onInputFocus?: () => unknown;
}): [
  TypeaheadState,
  (arg1: TypeaheadAction) => void,
  InputProps & {
    value: string;
    onChange: (event: React.SyntheticEvent<HTMLInputElement>) => void;
  },
  (arg1: React.MouseEvent<HTMLInputElement>) => void,
] {
  const [state, dispatch] = React.useReducer(
    reduceWithSearchTerm,
    null,
    getDefaultState,
  );

  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const inputElement = React.useRef<HTMLInputElement | null | undefined>();
  const {focused} = state;

  const handleFocus = (_event: React.SyntheticEvent<HTMLInputElement>) => {
    dispatch({type: 'focus'});
    onInputFocus && onInputFocus();
  };

  const handleBlur = (_event: React.SyntheticEvent<HTMLInputElement>) => {
    dispatch({type: 'blur'});
    if (state.searchTerm) {
      onAddValue(state.searchTerm);
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const key = event.key;

    if (proceedKeys.has(key)) {
      if (value.trim()) {
        event.preventDefault();
        dispatch({type: 'search', payload: ''});
        onAddValue(value);
      }
    } else if (key === 'Backspace') {
      if (!value) {
        onBackspace();
      }
    } else if (key === 'Escape') {
      event.preventDefault();
      dispatch({type: 'search', payload: ''});
      setTimeout(() => {
        inputElement.current && inputElement.current.blur();
      }, 0);
    }
  };

  const handleChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    dispatch({type: 'search', payload: event.currentTarget.value});
  };

  const handleContainerMouseDown = (
    event: React.SyntheticEvent<HTMLElement>,
  ) => {
    const input = inputElement.current;
    if (focused && input === document.activeElement && event.target !== input) {
      event.preventDefault();
    } else if (input && !focused) {
      event.preventDefault();
      input.focus();
      input.select();
    }
  };

  return [
    state,
    dispatch,
    {
      ref: inputElement,
      value: state.searchTerm,
      onKeyDown: handleInputKeyDown,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onChange: handleChange,
    },
    handleContainerMouseDown,
  ];
}

export function useSelect<V>({
  onAddOption = () => undefined,
  ...args
}: {
  options: V[];
  onAddOption?: (arg1: V) => unknown;
}): [
  TypeaheadState,
  InputProps,
  (arg1: React.MouseEvent<HTMLInputElement>) => void,
] {
  const [state, dispatch] = React.useReducer(
    reduceDefault,
    null,
    getDefaultState,
  );

  const [inputProps, handleContainerMouseDown] = useTypeahead({
    state,
    dispatch,
    onAddOption,
    ...args,
  });

  return [state, {readOnly: true, ...inputProps}, handleContainerMouseDown];
}

/*
export function useTypeaheadWithArbitraryValues({
  state,
  dispatch,
  options,
  onAddOption,
  validateArbitraryValue,
  resolveArbitraryOptionLabel,
}) {
  const {searchTerm} = state;
  const trimmedSearchTerm = searchTerm.trim();
  const hasValidArbitraryValue =
    trimmedSearchTerm && validateArbitraryValue(trimmedSearchTerm);

  if (hasValidArbitraryValue) {
    options = [
      {
        value: trimmedSearchTerm,
        label: resolveArbitraryOptionLabel(trimmedSearchTerm),
        arbitrary: true,
      },
      ...options,
    ];
  }

  const [{onBlur, ...inputProps}, ...hookStuff] = useTypeahead({
    state,
    dispatch,
    options,
    onAddOption,
  });

  const handleBlur = event => {
    if (hasValidArbitraryValue) {
      onAddOption({
        value: trimmedSearchTerm,
        label: trimmedSearchTerm,
        arbitrary: true,
      });
    }
    onBlur(event);
  };

  return [{onBlur: handleBlur, ...inputProps}, ...hookStuff];
}
*/

type TypeaheadState = {
  searchTerm: string;
  focused: boolean;
  selectedIndex: number;
};

type SuggestionsAction =
  | {
      type: 'focus';
    }
  | {
      type: 'blur';
    }
  | {
      type: 'select_index';
      payload: number;
    };

type TypeaheadAction =
  | SuggestionsAction
  | {
      type: 'search';
      payload: string;
    };

export const getDefaultState = (): TypeaheadState => ({
  searchTerm: '',
  focused: false,
  selectedIndex: 0,
});

export function reduceDefault(
  state: TypeaheadState,
  action: TypeaheadAction,
): {
  focused: boolean;
  searchTerm: string;
  selectedIndex: number;
} {
  switch (action.type) {
    case 'focus':
      return {...state, focused: true};
    case 'blur':
      return {...state, focused: false};
    case 'select_index':
      return {...state, selectedIndex: action.payload};
    default:
      throw new Error(`Invalid action type, ${action.type}`);
  }
}

export function reduceWithSearchTerm(
  state: TypeaheadState,
  action: TypeaheadAction,
): {
  focused: boolean;
  searchTerm: string;
  selectedIndex: number;
} {
  switch (action.type) {
    case 'search':
      return {...state, searchTerm: action.payload, selectedIndex: 0};
    case 'blur':
      return {...state, focused: false, searchTerm: '', selectedIndex: 0};
    default:
      return reduceDefault(state, action);
  }
}
